import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceNew } from 'src/utils/axios';
import { isIncomingTxFresh } from 'src/constants/transactionStatus';

export const fetchUserTransactions = createAsyncThunk(
  'transactions/fetchUserTransactions',
  async (_unused, { rejectWithValue }) => {
    try {
      /**
       * Session-based fetch.
       *
       * Do NOT require userId here anymore.
       * Backend should read:
       *   req.session.userId
       *   req.session.authType
       *   req.session.walletAddress
       */
      const res = await axiosInstanceNew.get('/transactions');

      return res.data?.transactions ?? res.data?.data ?? [];
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || 'Failed to load transactions'
      );
    }
  }
);

export const refreshUserTransactions = createAsyncThunk(
  'transactions/refreshUserTransactions',
  async (_unused, { rejectWithValue }) => {
    try {
      /**
       * Same endpoint as fetchUserTransactions.
       * Refresh should also be session-based.
       */
      const res = await axiosInstanceNew.get('/transactions');

      return res.data?.transactions ?? res.data?.data ?? [];
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || 'Failed to refresh transactions'
      );
    }
  }
);

export const fetchTransactionById = createAsyncThunk(
  'transactions/fetchTransactionById',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error('Transaction ID is required');
      const res = await axiosInstanceNew(`/transactions/${id}`);
      // The route returns the document directly (not wrapped)
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || 'Failed to load transaction'
      );
    }
  }
);

export const updateUserTransactions = createAsyncThunk(
  'transactions/updateUserTransactions',
  async (transactions, { rejectWithValue }) => {
    try {
      if (!Array.isArray(transactions)) {
        throw new Error('Transactions payload must be an array');
      }
      return transactions;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update transactions');
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async (transaction, { rejectWithValue }) => {
    try {
      if (!transaction || !transaction._id) {
        throw new Error('Transaction must have an _id');
      }
      return transaction;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update transaction');
    }
  }
);

const initialState = {
  items: [],
  meta: {},
  isLoading: false,
  isFetchingOne: false,
  fetchOneError: null,
  error: null,
  lastFetchedAt: null,
};

function toArray(value, fallback) {
  if (Array.isArray(value)) return value;
  return fallback;
}

function findTxIndex(items, txId) {
  if (!Array.isArray(items) || !txId) return -1;
  return items.findIndex((tx) => String(tx?._id) === String(txId));
}

// Merge an incoming copy into the list, ignoring copies older than the one we
// already hold (out-of-order socket / REST responses made statuses flicker
// backwards, e.g. "Sending" → "Queued").
function mergeAt(state, index, incoming) {
  const existing = state.items[index];
  if (!isIncomingTxFresh(existing, incoming)) return;

  state.items[index] = { ...existing, ...incoming };
}

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    prependTransaction(state, action) {
      const tx = action.payload;
      if (!tx?._id) return;

      if (!Array.isArray(state.items)) state.items = [];

      const index = findTxIndex(state.items, tx._id);

      // duplicate socket guard
      if (index !== -1) {
        mergeAt(state, index, tx);
        return;
      }

      state.items.unshift(tx);
    },

    mergeTransactionInPlace(state, action) {
      const updatedTx = action.payload;
      if (!updatedTx?._id) return;

      const index = findTxIndex(state.items, updatedTx._id);
      if (index === -1) return;

      mergeAt(state, index, updatedTx);
    },

    clearTransactions(state) {
      state.items = [];
      state.meta = {};
      state.isLoading = false;
      state.isFetchingOne = false;
      state.fetchOneError = null;
      state.error = null;
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchUserTransactions ──────────────────────────────────────────
      .addCase(fetchUserTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = toArray(action.payload, []);
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchUserTransactions.rejected, (state, action) => {
        // Keep whatever is already on screen — a flaky mobile request must not
        // wipe the user's history.
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to load transactions';
      })

      // ── refreshUserTransactions ────────────────────────────────────────
      .addCase(refreshUserTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshUserTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = toArray(action.payload, []);
        state.lastFetchedAt = Date.now();
      })
      .addCase(refreshUserTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to refresh transactions';
      })

      // ── fetchTransactionById ───────────────────────────────────────────
      // Fetches a single tx by Mongo _id. Used when navigating directly to
      // /transactions/:id (e.g. hard-refresh) and the item isn't in the store.
      // Merges the result into items so the list stays consistent.
      .addCase(fetchTransactionById.pending, (state) => {
        state.isFetchingOne = true;
        state.fetchOneError = null;
      })
      .addCase(fetchTransactionById.fulfilled, (state, action) => {
        state.isFetchingOne = false;
        const tx = action.payload;
        if (!tx?._id) return;

        if (!Array.isArray(state.items)) state.items = [];

        const index = findTxIndex(state.items, tx._id);
        if (index !== -1) {
          // Merge in case we already have a stale copy
          mergeAt(state, index, tx);
        } else {
          state.items.unshift(tx);
        }
      })
      .addCase(fetchTransactionById.rejected, (state, action) => {
        state.isFetchingOne = false;
        state.fetchOneError =
          action.payload || action.error?.message || 'Failed to load transaction';
      })

      // ── updateUserTransactions ─────────────────────────────────────────
      .addCase(updateUserTransactions.pending, (state) => {
        state.error = null;
      })
      .addCase(updateUserTransactions.fulfilled, (state, action) => {
        state.items = toArray(action.payload, state.items);
      })
      .addCase(updateUserTransactions.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ── updateTransaction ──────────────────────────────────────────────
      .addCase(updateTransaction.fulfilled, (state, action) => {
        const updatedTx = action.payload;
        const index = findTxIndex(state.items, updatedTx._id);
        if (index === -1) return;
        mergeAt(state, index, updatedTx);
      });
  },
});

export const { clearTransactions, prependTransaction, mergeTransactionInPlace } =
  transactionsSlice.actions;

export default transactionsSlice.reducer;

export const selectTransactions = (state) => state?.transactions?.items ?? [];
export const selectTransactionsLoading = (state) => state?.transactions?.isLoading ?? false;
export const selectTransactionsError = (state) => state?.transactions?.error ?? null;
export const selectTransactionsLastFetchedAt = (state) =>
  state?.transactions?.lastFetchedAt ?? null;

// Single-transaction selectors
export const selectTransactionById = (id) => (state) =>
  state?.transactions?.items?.find((t) => String(t?._id) === String(id)) ?? null;
export const selectIsFetchingOne = (state) => state?.transactions?.isFetchingOne ?? false;
export const selectFetchOneError = (state) => state?.transactions?.fetchOneError ?? null;
